import React from "react";
import { color, space, text, weight, leading, tracking, numeric } from "../tokens";

/**
 * <KpiBlock> — one metric, presented well.
 *
 *   label      : uppercase label above the number
 *   value      : the number (string or ReactNode). Auto tabular numerics.
 *   size       : "sm" (24px) | "md" (32px) default | "lg" (48px hero)
 *   accent     : token color name for the value (default text.primary)
 *   delta      : { value: "+3.2%" | -18, direction: "up" | "down", tone: "success"|"warning"|"danger"|"info" }
 *   sublabel   : optional caption below
 *   alert      : boolean — highlights the block as "attention needed"
 *
 *  Groups of 3 in a row. Miller's 7±2.
 */
export function KpiBlock({
  label,
  value,
  size = "md",
  accent,
  delta,
  sublabel,
  alert = false,
  style,
}) {
  const sizeMap = { sm: text["2xl"], md: text["3xl"], lg: text["4xl"] };
  const valueColor = accent || (alert ? color.danger.solid : color.text.primary);
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: space[1],
        ...style,
      }}
    >
      {label && (
        <span
          style={{
            fontSize: text.xs,
            fontWeight: weight.semibold,
            color: color.text.tertiary,
            letterSpacing: tracking.wider,
            textTransform: "uppercase",
          }}
        >
          {label}
        </span>
      )}
      <span
        style={{
          fontSize: sizeMap[size],
          fontWeight: weight.semibold,
          lineHeight: leading.tight,
          color: valueColor,
          letterSpacing: tracking.tight,
          ...numeric.tabular,
        }}
      >
        {value}
      </span>
      {delta && <KpiDelta {...delta} />}
      {sublabel && (
        <span
          style={{
            fontSize: text.xs,
            color: color.text.tertiary,
            marginTop: space[1],
          }}
        >
          {sublabel}
        </span>
      )}
    </div>
  );
}

const TONE_COLOR = {
  success: color.success.text,
  warning: color.warning.text,
  danger: color.danger.text,
  info: color.info.text,
  neutral: color.text.tertiary,
};

export function KpiDelta({ value, direction, tone = "neutral" }) {
  const arrow = direction === "up" ? "▲" : direction === "down" ? "▼" : "";
  return (
    <span
      style={{
        fontSize: text.xs,
        fontWeight: weight.semibold,
        color: TONE_COLOR[tone],
        display: "inline-flex",
        alignItems: "center",
        gap: space[1],
        ...numeric.tabular,
      }}
    >
      {arrow && <span style={{ fontSize: 9 }}>{arrow}</span>}
      {value}
    </span>
  );
}
